/* eslint-disable no-console */

import { AfterViewInit, Component, EventEmitter, Input, NgZone, Output } from '@angular/core';
import { Map, View } from 'ol';
import { Coordinate, format } from 'ol/coordinate';
import { Attribution, defaults as DefaultControls, FullScreen, MousePosition, ScaleLine, Zoom, ZoomSlider } from 'ol/control';
import { fromLonLat, toLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import TileWMS from 'ol/source/TileWMS';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import LayerSwitcher, { GroupLayerOptions } from 'ol-layerswitcher';
import LayerGroup from 'ol/layer/Group';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { getValueOrError } from '@core/errors/exception';
import Select from 'ol/interaction/Select';
import { TetraSourceService } from '@shared/map-layers/tetra-source.service';
import { MarcatoriSourceService } from '@shared/map-layers/marcatori-source.service';
import { tipoMarcatoreList } from '@core/utils/marcatore.utils';
import { SelettivaMarcatoriSourceService } from '@shared/map-layers/selettiva-marcatori-source.service';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { PermissionCheckerService } from '@shared/permissions/permission-checker.service';
import { Permesso, PermessoValore } from '@shared/types/auth.models';
import { NgxAuthService } from 'ngx-auth-utils';
import BaseLayer from 'ol/layer/Base';
import ClusterSource from 'ol/source/Cluster';

interface LastProductsByType {
    total: number;
    lastProducts: Array<LastProductByType>;
}

interface LastProductByType {
    productType: string;
    time: number;
    period: string;
}

interface LastUpdate {
    code: string;
    radar: string;
    lastUpdate: string;
}

const BING_API_KEY = 'AsAH6rRTGHZ0UkPTRsyP7Sw4_AHESq7bdakU3lYXd-9guxqA3xz0TxrU-QUG-Nnr';

@Component({
    selector: 'app-ol-map',
    templateUrl: './ol-map.component.html',
    styleUrls: ['./ol-map.component.scss'],
})
export class OlMapComponent implements AfterViewInit {
    @Input() center!: Coordinate;
    @Input() zoom?: number;
    @Input() disableControls?: boolean;
    view?: View;
    Map?: Map;
    @Output() mapReady = new EventEmitter<Map>();
    tetraLayer = new VectorLayer({});
    marcatoriLayer = new VectorLayer({});
    marcatoriLayerFiltered = new VectorLayer({});
    selettiveLayer = new VectorLayer({});
    selettiveLayerFiltered = new VectorLayer({});

    public hasPermissionToManageMarcatore = false;

    // HRD,VMI,SRI,SRT1,SRT3,SRT6,SRT12,SRT24,IR108,RADAR_STATUS,AMV,TEMP,LTG
    private lastUpdates: Array<LastUpdate> = [
        { code: 'TMP', radar: 'temperature', lastUpdate: '' },
        { code: 'HRD', radar: 'hrd', lastUpdate: '' },
        { code: 'CUM24', radar: 'srt24', lastUpdate: '' },
        { code: 'SRT1', radar: 'srt1', lastUpdate: '' },
        { code: 'VMI', radar: 'vmi', lastUpdate: '' },
    ];

    constructor(
        private zone: NgZone,
        private http: HttpClient,
        private tetraSourceService: TetraSourceService,
        private permissionChecker: PermissionCheckerService,
        private selettivaMarcatoriSourceService: SelettivaMarcatoriSourceService,
        private marcatoriSourceService: MarcatoriSourceService,
        private activatedRoute: ActivatedRoute,
        private authService: NgxAuthService,
        private router: Router,
    ) {}

    ngAfterViewInit(): void {
        console.log('[Radar Map] Inizializzazione componente mappa...');

        this.hasPermissionToManageMarcatore = this.permissionChecker.hasThePermission(
            Permesso.Mappa,
            PermessoValore.Modifica,
            this.authService.snapshot.user.tokenData.permessoList,
        );

        console.log('[Radar Map] Permessi marcatore:', this.hasPermissionToManageMarcatore);

        // disabilitato perché il servizio tetra è stato disabilitato
        // this.tetraSourceService.getSource().subscribe((tetraSource) => {
        //     this.tetraLayer.setSource(tetraSource);
        // });

        this.marcatoriSourceService.getSource([1, 2, 3, 4, 5, 100]).subscribe((marcatoriSource) => {
            this.marcatoriLayer.setSource(marcatoriSource);
            this.marcatoriLayer.setStyle((features) => {
                return this.marcatoriSourceService.getClusterStyle(features);
            });
            this.marcatoriLayerFiltered.set('visible', true);
            this.marcatoriLayerFiltered.setSource(marcatoriSource);
            this.marcatoriLayerFiltered.setStyle((features) => {
                return this.marcatoriSourceService.getClusterStyle(features);
            });
            console.log('[Radar Map] Layer marcatori caricati');
        });

        // Selettiva Marcatori
        this.selettivaMarcatoriSourceService.getSource(30).subscribe((selettivaMarcatoriSource) => {
            this.selettiveLayer.setSource(selettivaMarcatoriSource);
            this.selettiveLayer.setStyle((features) => {
                return this.selettivaMarcatoriSourceService.getClusterStyle(features);
            });

            this.selettiveLayerFiltered.setSource(this.selettivaMarcatoriSourceService.filterByDays(this.selettiveLayer, 0));
            this.selettiveLayerFiltered.setStyle((features) => {
                return this.selettivaMarcatoriSourceService.getClusterStyle(features);
            });
            console.log('[Radar Map] Layer selettive caricati');
        });

        console.log('[Radar Map] Inizializzazione mappa OpenLayers...');
        if (!this.Map) {
            this.zone.runOutsideAngular(() => this.initMap());
        }

        console.log('[Radar Map] Richiesta ultimi aggiornamenti API Radar DPC...');
        console.log(
            '[Radar Map] Codici prodotto richiesti:',
            this.lastUpdates.map((u) => u.code),
        );
        const requests: Array<Observable<LastProductsByType>> = [];

        this.lastUpdates.forEach((val) => {
            requests.push(
                this.http.get<LastProductsByType>(`https://radar-api.protezionecivile.it/findLastProductByType?type=${val.code}`),
            );
        });

        forkJoin(requests).subscribe({
            next: (responses) => {
                console.log('[Radar Map] Ricevute risposte API:', responses.length, 'prodotti');
                this.setLastUpdate(responses);
                console.log('[Radar Map] Aggiornamento timestamp layer radar...');
                this.updateRadarLayersTime();
                console.log('[Radar Map] Timestamp aggiornati con successo');
            },
            error: (error) => {
                console.error('[Radar Map] Errore caricamento dati radar:', error);
                console.warn('[Radar Map] Mappa comunque visualizzata con timestamp default');
            },
        });

        console.log('[Radar Map] Emissione evento mapReady');
        setTimeout(() => this.mapReady.emit(this.Map));
    }

    private initMap(): void {
        this.readFromUrlParams();

        this.view = new View({
            center: fromLonLat(this.center),
            zoom: this.zoom,
        });

        /**
         * INIZIALIZZO LAYERS
         */

        // MAPPE DI BASE
        const baseMaps = new LayerGroup({
            title: 'Mappe di base',
            visible: true,
        } as GroupLayerOptions);

        // Define layers with names, sources, and visibility
        const TMPLayersName: Array<{
            name: string;
            source: XYZ | OSM;
            visible?: boolean;
        }> = [
            {
                name: 'OSM Standard',
                source: new OSM({
                    attributions: '© OpenStreetMap contributors',
                }),
                visible: true,
            },
            {
                name: 'CartoDB Light',
                source: new XYZ({
                    url: 'https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
                    attributions: '© OpenStreetMap contributors © CARTO',
                }),
                visible: false,
            },
            {
                name: 'CartoDB Dark',
                source: new XYZ({
                    url: 'https://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
                    attributions: '© OpenStreetMap contributors © CARTO',
                }),
                visible: false,
            },
            {
                name: 'Elevazione',
                source: new XYZ({
                    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
                    attributions: '© OpenStreetMap contributors, SRTM | Map style: OpenTopoMap (CC-BY-SA)',
                    maxZoom: 17,
                }),
                visible: false,
            },
        ];

        // Loop and create tile layers
        TMPLayersName.forEach((element) => {
            const layerTMP = new TileLayer({
                visible: element.visible || false,
                preload: Infinity,
                source: element.source,
            });

            layerTMP.set('title', element.name);
            layerTMP.set('type', 'base');

            baseMaps.getLayers().push(layerTMP);
        });

        // MAPPE ACCESSORIE PROVINCIA
        // stem:ammcom non c'è più
        const accessoryMaps = new LayerGroup({
            title: 'Mappe provinciali',
            fold: 'close',
            combine: false,
        } as GroupLayerOptions);

        const TMPLayersNameProvincia = [
            { name: 'Confine provinciale', value: 'stem:ammprv', visible: false },
            { name: 'Confini comuni', value: 'stem:ammcom' },
            { name: 'Toponimi', value: 'stem:toponimi', visible: false },
            { name: 'Carta tecnica provinciale 2020', value: 'stem:ctp2020_bn_00' },
            { name: 'Ortofoto 1973', value: 'stem:ofdtn73' },
            { name: 'Ortofoto 1994', value: 'stem:ofdtn94' },
            { name: 'Ortofoto 2015', value: 'stem:ecw-rgb-2015' },
        ];

        TMPLayersNameProvincia.forEach((element) => {
            const layerTMP = new TileLayer({
                visible: false,
                source: new TileWMS({
                    url: 'https://siat.provincia.tn.it/geoserver/stem/wms',
                    params: {
                        FORMAT: 'image/png',
                        VERSION: '1.1.1',
                        STYLES: '',
                        LAYERS: element.value,
                    },
                    serverType: 'geoserver',
                }),
            });
            layerTMP.set('title', element.name);
            layerTMP.setVisible(element.visible || false);

            accessoryMaps.getLayers().push(layerTMP);
        });

        // RADAR PROTEZIONE CIVILE
        const radarPCMaps = new LayerGroup({
            title: 'Radar Meteo Protezione Civile',
            fold: 'close',
            combine: false,
        } as GroupLayerOptions);

        const TMPLayersNameProtezioneCivile = this.getRadarLayersConfig();

        TMPLayersNameProtezioneCivile.forEach((element) => {
            const lastUpdateTime = this.findLastUpdate(element);

            const wmsParams: Record<string, string> = {
                FORMAT: 'image/png',
                VERSION: '1.1.1',
                STYLES: '',
                LAYERS: 'radar:' + element.value,
            };

            if (lastUpdateTime) {
                wmsParams.TIME = lastUpdateTime;
                console.log(`[Radar Layer WMS] ${element.name}: TIME impostato a ${lastUpdateTime}`);
            } else {
                console.log(`[Radar Layer WMS] ${element.name}: TIME non impostato (userà default server)`);
            }

            const layerTMP = new TileLayer({
                visible: false,
                source: new TileWMS({
                    url: 'https://radar-geowebcache.protezionecivile.it/service/wms',
                    params: wmsParams,
                    serverType: 'geoserver',
                }),
            });

            console.log(`[Radar Layer] Creato layer: ${element.name} con value: ${element.value}`);
            layerTMP.set('title', element.name);
            layerTMP.setVisible(element.visible || false);
            radarPCMaps.getLayers().push(layerTMP);
        });

        // Marcatori
        const marcatoriMaps = new LayerGroup({
            title: 'Marcatori',
            visible: true,
            combine: false,
        } as GroupLayerOptions);

        const TMPLayersNameMarcatori = tipoMarcatoreList.map((tipo) => ({
            name: tipo.label,
            value: tipo.value.toString(),
        }));

        TMPLayersNameMarcatori.forEach((element) => {
            const layerTMP = new VectorLayer({});
            layerTMP.set('title', element.name);
            layerTMP.set('type', 'marcatore');
            layerTMP.setVisible(true);
            marcatoriMaps.getLayers().push(layerTMP);
        });

        marcatoriMaps.getLayers().push(this.marcatoriLayerFiltered);

        marcatoriMaps.on('change:visible', () => {
            const someAreVisible = marcatoriMaps
                .getLayers()
                .getArray()
                .some((l: BaseLayer) => l.get('type') === 'marcatore' && l.getVisible());
            if (!someAreVisible) {
                marcatoriMaps.getLayers().forEach((layer: BaseLayer) => {
                    if (layer.get('type') === 'marcatore') {
                        layer.setVisible(marcatoriMaps.getVisible());
                    }
                });
            }
        });

        marcatoriMaps.getLayers().forEach((layer: BaseLayer) => {
            layer.on('change:visible', () => {
                const typeMarcatoreVisible = marcatoriMaps
                    .getLayers()
                    .getArray()
                    .filter((l: BaseLayer) => l.get('type') === 'marcatore' && l.getVisible())
                    .map((l: BaseLayer) => l.get('title'));
                const typeMarcatoreListVisible = TMPLayersNameMarcatori.filter((t) => typeMarcatoreVisible.includes(t.name)).map(
                    (t) => t.value,
                );

                this.marcatoriLayerFiltered.setSource(
                    this.marcatoriSourceService.filterByTipoMarcatore(this.marcatoriLayer, typeMarcatoreListVisible),
                );
                this.marcatoriLayerFiltered.setStyle((features) => {
                    return this.marcatoriSourceService.getClusterStyle(features);
                });

                if (!marcatoriMaps.getVisible() && layer.get('type') === 'marcatore' && layer.getVisible()) {
                    this.marcatoriLayerFiltered.setVisible(true);
                }
            });
        });

        const selettiveMaps = new LayerGroup({
            title: 'Selettive',
            fold: 'open',
            combine: false,
        } as GroupLayerOptions);

        // Create selector-only layers
        const TMPLayersNameSelettive = [
            { name: 'Selettive (Ultimo giorno)', value: '1' },
            { name: 'Selettive (Ultima settimana)', value: '7' },
            { name: 'Selettive (Ultimo mese)', value: '30' },
        ];

        TMPLayersNameSelettive.forEach((element) => {
            const toggleLayer = new VectorLayer({});
            toggleLayer.set('title', element.name);
            toggleLayer.set('days', element.value);
            toggleLayer.set('type', 'selettiva');
            toggleLayer.setVisible(false); // none selected by default
            selettiveMaps.getLayers().push(toggleLayer);
        });

        selettiveMaps.getLayers().push(this.selettiveLayerFiltered);

        selettiveMaps.getLayers().forEach((layer: BaseLayer) => {
            layer.on('change:visible', () => {
                // Make sure only one selettiva layer is visible at a time
                if (layer.getVisible()) {
                    selettiveMaps.getLayers().forEach((other) => {
                        if (other !== layer && other.getVisible() && other.get('type') === 'selettiva') {
                            other.setVisible(false);
                        }
                    });
                }

                // Find the currently visible selettiva layer (if any)
                const visibleLayer = selettiveMaps
                    .getLayers()
                    .getArray()
                    .find((l) => l.getVisible() && l.get('type') === 'selettiva');

                const days = visibleLayer ? parseInt(visibleLayer.get('days'), 10) : 0;

                // Update the filtered source accordingly
                const filteredSource = this.selettivaMarcatoriSourceService.filterByDays(this.selettiveLayer, days);

                this.selettiveLayerFiltered.setSource(filteredSource);
                this.selettiveLayerFiltered.setStyle((features) => {
                    return this.selettivaMarcatoriSourceService.getClusterStyle(features);
                });
            });
        });

        // Servizio Tetra nin funzionante
        // this.tetraLayer.set('title', 'TeTRa');
        // this.tetraLayer.setVisible(false);
        // servizioMaps.getLayers().push(this.tetraLayer);

        /**
         * CREO MAPPA
         */
        this.Map = new Map({
            layers: [baseMaps, radarPCMaps, accessoryMaps, marcatoriMaps, selettiveMaps],
            target: 'map',
            pixelRatio: 1,
            view: this.view,
            controls: [],
        });

        const control = DefaultControls().extend([
            new Attribution(),
            new MousePosition({
                projection: 'EPSG:4326',
                coordinateFormat: function (coordinate: number[] | undefined): string {
                    return format(coordinate || [0, 0], '{y}, {x}', 4);
                },
            }),
            new ScaleLine(),
            new Zoom(),
            new ZoomSlider(),
            new FullScreen(),
        ]);
        const layerSwitcher = new LayerSwitcher({
            activationMode: 'click',
            reverse: true,
            groupSelectStyle: 'children',
        });

        // gestione del click su una feature
        const select = new Select();
        this.Map.addInteraction(select);
        // Here's the event handler that will give you the selected features
        select.on('select', function () {
            // console.log(e.selected);
        });

        if (!this.disableControls) {
            control.forEach((c) => this.Map?.addControl(c));

            this.Map.addControl(layerSwitcher);
        }

        this.rightClickMenu();

        this.clickOnMarcatore();
    }

    private findLastUpdate(element: { name: string; value: string }): string | undefined {
        const update = this.lastUpdates.find((s) => s.radar.toLocaleLowerCase() === element.value.toLocaleLowerCase());

        if (!update) {
            console.warn(`[Radar Layer] Nessun aggiornamento trovato per layer: ${element.name} (${element.value})`);
            return undefined;
        }

        if (!update.lastUpdate) {
            console.info(`[Radar Layer] Timestamp non ancora caricato per: ${element.name} - userà default del server`);
            return undefined;
        }

        console.log(`[Radar Layer] Timestamp caricato per ${element.name}: ${update.lastUpdate}`);
        return update.lastUpdate;
    }

    private getRadarLayersConfig(): Array<{ name: string; value: string; visible?: boolean }> {
        return [
            { name: 'Vento', value: 'amv' },
            { name: 'Copertura nuvolosa', value: 'ir108' },
            { name: 'Temperature', value: 'temperature' },
            { name: 'Fulminazioni', value: 'ltg' },
            { name: 'Temporali', value: 'hrd' },
            { name: 'Precipitazioni 24h', value: 'srt24' },
            { name: 'Precipitazioni 1h', value: 'srt1' },
            { name: 'Massima riflettività', value: 'vmi', visible: false },
        ];
    }

    private updateRadarLayersTime(): void {
        console.log('[Radar Map] updateRadarLayersTime() - Inizio aggiornamento timestamp layer...');

        const radarLayersConfig = this.getRadarLayersConfig();
        const radarLayers = this.Map?.getLayers()
            .getArray()
            .filter(
                (layerGroup: BaseLayer) => layerGroup instanceof LayerGroup && layerGroup.get('title') === 'Radar Meteo Protezione Civile',
            );

        if (!radarLayers || radarLayers.length === 0) {
            console.warn('[Radar Map] Nessun gruppo layer "Radar Meteo Protezione Civile" trovato');
            return;
        }

        console.log(`[Radar Map] Trovato gruppo layer radar con ${radarLayers.length} elementi`);

        const radarGroup = radarLayers[0] as LayerGroup;
        let updatedCount = 0;

        radarGroup.getLayers().forEach((layer: BaseLayer) => {
            const radarName = layer.get('title');
            const element = radarLayersConfig.find((e) => e.name === radarName);

            if (element && layer instanceof TileLayer) {
                const source = layer.getSource() as TileWMS;
                if (source) {
                    const newTime = this.findLastUpdate(element);
                    if (newTime) {
                        source.updateParams({ TIME: newTime });
                        console.log(`[Radar Map] Layer "${radarName}" aggiornato: TIME = ${newTime}`);
                        updatedCount++;
                    } else {
                        console.warn(`[Radar Map] Layer "${radarName}" non aggiornato: timestamp non disponibile`);
                    }
                }
            }
        });

        console.log(`[Radar Map] Aggiornamento completato: ${updatedCount} layer aggiornati`);
    }

    private setLastUpdate(responses: Array<LastProductsByType>): void {
        console.log('[Radar Map] setLastUpdate() - Elaborazione', responses.length, 'risposte...');

        responses.forEach((response, index) => {
            console.log(`[Radar Map] Risposta ${index + 1}/${responses.length}:`, response);

            if (response.lastProducts[0]) {
                const productType = response.lastProducts[0].productType;
                const productTime = new Date(response.lastProducts[0].time).toISOString();
                console.log(`[Radar Map] Prodotto ${productType}: timestamp = ${productTime}`);

                const lastUpdate = getValueOrError(this.lastUpdates.find((s) => s.code === response.lastProducts[0].productType));
                lastUpdate.lastUpdate = productTime;
            } else {
                console.warn(`[Radar Map] Nessun prodotto trovato nella risposta ${index + 1}`);
            }
        });

        console.log('[Radar Map] Aggiunta layer derivati (LTG, AMV, IR108)...');
        this.lastUpdates.push({
            code: 'ltg',
            radar: 'ltg',
            lastUpdate: getValueOrError(this.lastUpdates.find((s) => s.radar === 'vmi')).lastUpdate,
        });
        this.lastUpdates.push({
            code: 'amv',
            radar: 'amv',
            lastUpdate: getValueOrError(this.lastUpdates.find((s) => s.radar === 'vmi')).lastUpdate,
        });
        this.lastUpdates.push({
            code: 'ir108',
            radar: 'ir108',
            lastUpdate: getValueOrError(this.lastUpdates.find((s) => s.radar === 'vmi')).lastUpdate,
        });

        console.log('[Radar Map] Totale timestamp caricati:', this.lastUpdates.length);
    }

    private rightClickMenu(): void {
        // Prevent right click menu on map
        const mapElement = document.getElementById('map');

        if (mapElement && this.Map) {
            const contextMenu = document.createElement('div');
            contextMenu.id = 'custom-context-menu';
            contextMenu.className = 'dropdown-menu show';
            contextMenu.style.position = 'absolute';
            contextMenu.style.display = 'none';

            const menuItem = document.createElement('a');
            menuItem.className = 'dropdown-item';
            menuItem.textContent = 'Aggiungi un nuovo marcatore';

            contextMenu.appendChild(menuItem);
            document.body.appendChild(contextMenu);

            document.addEventListener('click', () => {
                contextMenu.style.display = 'none';
            });

            mapElement.addEventListener('contextmenu', (event) => {
                event.preventDefault();

                if (!this.Map) {
                    return;
                }

                const pixel = [event.offsetX, event.offsetY];
                const coordinate = this.Map.getCoordinateFromPixel(pixel);
                const [lon, lat] = toLonLat(coordinate);
                const zoom = this.Map?.getView().getZoom();
                const center = this.Map?.getView().getCenter();
                const centerLatLon = toLonLat(center || [11, 46]);
                const centerLat = centerLatLon[1];
                const centerLon = centerLatLon[0];

                // Costruisci l’URL con i parametri lon/lat
                menuItem.href = `/mappa/marcatore/new?lon=${lon}&lat=${lat}&zoom=${zoom}&centerLat=${centerLat}&centerLon=${centerLon}`;

                contextMenu.style.left = `${event.pageX}px`;
                contextMenu.style.top = `${event.pageY}px`;
                contextMenu.style.display = 'block';
            });
        }
    }

    private clickOnMarcatore(): void {
        this.Map?.on('singleclick', (evt) => {
            this.Map?.forEachFeatureAtPixel(evt.pixel, (feature) => {
                if (!feature) {
                    return undefined; // Nessuna feature trovata, quindi non faccio nulla
                }

                const features = feature.get('features');

                if (features[0].get('selettivaData') !== undefined) {
                    this.selettivaMarcatoriSourceService.openPupUp(this.Map, feature);
                } else if (features[0].get('data') !== undefined) {
                    this.marcatoriSourceService.openPupUp(this.Map, feature);
                }

                return undefined;
            });
        });

        // Chiudi popup su click fuori
        this.Map?.on('click', (evt) => {
            const pixel = evt.pixel;
            let clickedOnFeature = false;
            this.Map?.forEachFeatureAtPixel(pixel, () => {
                clickedOnFeature = true;
            });

            if (!clickedOnFeature) {
                this.Map?.getOverlays().clear();
            }
        });
    }

    private readFromUrlParams(): void {
        const x = parseFloat(this.activatedRoute.snapshot.queryParams['centerLon']);
        const y = parseFloat(this.activatedRoute.snapshot.queryParams['centerLat']);
        const zoom = parseFloat(this.activatedRoute.snapshot.queryParams['zoom']);

        if (!isNaN(x) && !isNaN(y)) {
            this.center = [x, y];
        }

        if (!isNaN(zoom)) {
            this.zoom = zoom;
        }
    }
}
